import React, { useState } from 'react'
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Fab,
  Typography
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import { AsnReceptionSkuHistoryDto } from '../types'
import { formatExpirationDate } from '../../../utils/dateHelper'

interface ViewDetailStepProps {
  asnNumber: string
  lpn: string
  hasLpn: boolean
  history: AsnReceptionSkuHistoryDto[]
  loading: boolean
  onClose: () => void
}

const isLong = (val: string) => val.length > 15

export const ViewDetailStep: React.FC<ViewDetailStepProps> = ({
  asnNumber,
  lpn,
  hasLpn,
  history,
  loading,
  onClose
}) => {
  const [expandedFefo, setExpandedFefo] = useState<Set<string>>(new Set())

  const toggleFefo = (key: string) => {
    setExpandedFefo((prev) => {
      const next = new Set(prev)
      next.has(key) ? next.delete(key) : next.add(key)
      return next
    })
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{ padding: '12px 16px 4px 16px', flexShrink: 0 }}>
        <Typography variant='body2' sx={{ fontWeight: 500, color: '#333' }}>
          Mostrar Ubicados
        </Typography>
      </div>
      {/* Barra superior */}
      <Box
        sx={{
          borderBottom: '1px solid #e0e0e0',
          display: 'flex',
          alignItems: 'center',
          px: 1,
          py: '6px',
          minHeight: '48px'
        }}
      >
        <Fab
          variant='extended'
          size='medium'
          onClick={onClose}
          style={{
            borderRadius: '12px',
            height: '40px',
            paddingLeft: '10px',
            paddingRight: '14px',
            backgroundColor: 'transparent',
            border: '1px solid #E0E0E0',
            boxShadow: 'none',
            gap: '6px',
            fontSize: '14px',
            fontWeight: 500,
            color: '#666',
            textTransform: 'none'
          }}
        >
          <ArrowBackIcon style={{ fontSize: 18, color: '#1976d2' }} />
          Volver
        </Fab>
      </Box>

      {/* Chips ASN / LPN */}
      <Box
        sx={{
          px: 1.5,
          py: '10px',
          display: 'grid',
          gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
          gap: '6px'
        }}
      >
        <Box sx={{ gridColumn: isLong(asnNumber) ? 'span 2' : 'span 1' }}>
          <Chip
            label={`ASN: ${asnNumber}`}
            color={hasLpn ? 'primary' : 'secondary'}
            variant='outlined'
            style={{ fontSize: '0.8125rem', width: '100%' }}
          />
        </Box>
        {lpn && (
          <Box sx={{ gridColumn: isLong(lpn) ? 'span 2' : 'span 1' }}>
            <Chip
              label={`LPN: ${lpn}`}
              color='primary'
              variant='outlined'
              style={{ fontSize: '0.8125rem', width: '100%' }}
            />
          </Box>
        )}
      </Box>

      {/* Contenido */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          px: 1.5,
          py: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5
        }}
      >
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', pt: 6 }}>
            <CircularProgress />
          </Box>
        ) : history.length === 0 ? (
          <Typography
            variant='h5'
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '300px',
              textAlign: 'center',
              px: 3
            }}
          >
            No hay registros de SKU(s) ubicados
          </Typography>
        ) : (
          <>
            {history.some((h) => h.excessQty > 0) && (
              <Box
                sx={{
                  backgroundColor: '#fff0f0',
                  border: '1px solid #ffcccc',
                  borderRadius: '8px',
                  p: 1.5,
                  display: 'flex',
                  gap: 1
                }}
              >
                <WarningAmberIcon
                  sx={{ color: 'error.main', fontSize: 18, mt: 0.2 }}
                />
                <Typography
                  variant='body2'
                  sx={{ color: 'error.main', fontWeight: 500 }}
                >
                  Las unidades sobrantes son sólo informativas y no serán
                  ingresadas al stock. Para ingresarlas debes crear una nueva
                  ASN con dichas unidades.
                </Typography>
              </Box>
            )}
            {history.map((h, i) => {
              const hasFefo = h.positionList.some((pos) =>
                pos.batchList.some((b) => b.batchId)
              )
              return (
                <Box
                  key={i}
                  sx={{
                    border: '1px solid #e0e0e0',
                    borderRadius: '10px',
                    overflow: 'hidden'
                  }}
                >
                  {/* Header SKU */}
                  <Box
                    sx={{
                      px: 2,
                      py: 1.2,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant='body2' fontWeight='bold'>
                        {h.sku}
                      </Typography>
                      {hasFefo && (
                        <Chip
                          label='• FEFO disponible'
                          size='small'
                          sx={{
                            backgroundColor: '#e8f5e9',
                            color: '#2e7d32',
                            fontWeight: 600,
                            fontSize: '0.7rem',
                            height: 20,
                            borderRadius: '10px'
                          }}
                        />
                      )}
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography variant='h6' fontWeight='bold'>
                        {h.qty}
                      </Typography>
                      <Typography
                        variant='caption'
                        color='text.secondary'
                        sx={{
                          textTransform: 'uppercase',
                          letterSpacing: 0.2,
                          fontSize: '0.6rem'
                        }}
                      >
                        Cantidad
                      </Typography>
                    </Box>
                  </Box>
                  {/* Posiciones */}
                  <Box sx={{ mx: 1.5, mb: 1, border: '1px solid #c5d8f7', borderRadius: '8px', overflow: 'hidden' }}>
                  {h.positionList.map((pos, pi) => {
                    const fefoKey = `${i}-${pi}`
                    const isFefoOpen = expandedFefo.has(fefoKey)
                    const posHasFefo = pos.batchList.some((b) => b.batchId)
                    return (
                      <Box key={pi} sx={{ borderTop: '1px solid #d0d0d0', backgroundColor: '#e8f0fb' }}>
                        <Box
                          sx={{
                            backgroundColor: 'transparent',
                            px: 2,
                            py: 1,
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                          }}
                        >
                          <Chip
                            label={pos.positionName}
                            size='small'
                            sx={{
                              backgroundColor: '#004bb1',
                              color: '#fff',
                              fontWeight: 700,
                              borderRadius: '6px'
                            }}
                          />
                          <Box sx={{ textAlign: 'right', lineHeight: 0.8 }}>
                            <Typography variant='body2' fontWeight='bold' sx={{ lineHeight: 1 }}>
                              {pos.qty}
                            </Typography>
                            <Typography
                              variant='caption'
                              color='text.secondary'
                              sx={{ textTransform: 'uppercase', letterSpacing: 0.2, fontSize: '0.5rem', lineHeight: 1 }}
                            >
                              Cantidad
                            </Typography>
                          </Box>
                        </Box>
                        {posHasFefo && (
                          <Box
                            sx={{ px: 2, py: 0.5 }}
                          >
                            <Button
                              size='small'
                              fullWidth
                              variant='text'
                              onClick={() => toggleFefo(fefoKey)}
                              sx={{
                                color: '#004bb1',
                                textTransform: 'none',
                                fontSize: '0.8rem',
                                borderRadius: '8px',
                                border: '1px solid #e0e0e0',
                                backgroundColor: '#fff'
                              }}
                            >
                              {isFefoOpen
                                ? 'Ocultar detalle FEFO'
                                : 'Mostrar detalle FEFO'}
                            </Button>
                          </Box>
                        )}
                        {isFefoOpen && (
                          <Box
                            sx={{ px: 2, py: 1, backgroundColor: '#f1f8e9' }}
                          >
                            <Box
                              sx={{
                                display: 'grid',
                                gridTemplateColumns: '1fr 1fr 1fr',
                                gap: 0.5,
                                mb: 0.5
                              }}
                            >
                              {['LOTE', 'VENCIMIENTO', 'CANTIDAD'].map(
                                (col) => (
                                  <Typography
                                    key={col}
                                    variant='caption'
                                    sx={{
                                      fontWeight: 700,
                                      color: '#555',
                                      fontSize: '0.65rem',
                                      textTransform: 'uppercase',
                                      letterSpacing: 0.5
                                    }}
                                  >
                                    {col}
                                  </Typography>
                                )
                              )}
                            </Box>
                            {pos.batchList.map((b, bi) => (
                              <Box
                                key={bi}
                                sx={{
                                  display: 'grid',
                                  gridTemplateColumns: '1fr 1fr 1fr',
                                  gap: 0.5,
                                  py: 0.5,
                                  borderTop:
                                    bi > 0 ? '1px solid #c8e6c9' : 'none'
                                }}
                              >
                                <Typography
                                  sx={{
                                    color: '#2e7d32',
                                    fontWeight: 600,
                                    fontSize: '0.72rem'
                                  }}
                                >
                                  {b.batchNumber}
                                </Typography>
                                <Typography
                                  sx={{ color: '#2e7d32', fontSize: '0.72rem' }}
                                >
                                  {b.expirationDate
                                    ? formatExpirationDate(
                                        b.expirationDate,
                                        'dd-MM-yyyy'
                                      )
                                    : '—'}
                                </Typography>
                                <Typography
                                  sx={{
                                    color: '#2e7d32',
                                    fontWeight: 600,
                                    fontSize: '0.72rem'
                                  }}
                                >
                                  {b.qty}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        )}
                      </Box>
                    )
                  })}
                  </Box>
                  {/* Sobrantes */}
                  {h.excessQty > 0 && (
                    <Box
                      sx={{
                        px: 2,
                        py: 1,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        borderTop: '1px dashed #ffaaaa',
                        backgroundColor: '#fff8f8'
                      }}
                    >
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                      >
                        <WarningAmberIcon
                          sx={{ color: 'error.main', fontSize: 16 }}
                        />
                        <Typography
                          variant='body2'
                          sx={{ color: 'error.main', fontWeight: 600 }}
                        >
                          Sobrantes
                        </Typography>
                      </Box>
                      <Typography
                        variant='body2'
                        sx={{ color: 'error.main', fontWeight: 700 }}
                      >
                        {h.excessQty} uds.
                      </Typography>
                    </Box>
                  )}
                </Box>
              )
            })}
          </>
        )}
      </Box>
    </Box>
  )
}
